import { createContext, forwardRef, type ReactNode } from 'react';

import { cva } from 'cva';
import {
  type ContextValue,
  useContextProps,
} from 'react-aria-components/slots';
import type { ToggleButtonProps as RACToggleButtonProps } from 'react-aria-components/ToggleButton';
import { ToggleButton as RACToggleButton } from 'react-aria-components/ToggleButton';

import styles from './toggle_button.module.css';

export const toggleButtonVariants = cva({
  base: styles.toggle_button,
  variants: {
    size: {
      l: styles.size_l,
      m: styles.size_m,
      s: styles.size_s,
      xs: styles.size_xs,
    },
    prominent: {
      true: styles.prominent,
    },
    variant: {
      stroke: styles.variant_stroke,
      ghost: styles.variant_ghost,
    },
    iconPosition: {
      start: null,
      top: styles.icon_position_top,
    },
    withIcon: {
      true: styles.with_icon,
    },
    withEndIcon: {
      true: styles.with_end_icon,
    },
    iconOnly: {
      true: styles.icon_only,
    },
    fullWidth: {
      true: styles.full_width,
    },
  },
  defaultVariants: {
    size: 'm',
    variant: 'ghost',
  },
});

export type ToggleButtonProps = RACToggleButtonProps & {
  children?: ReactNode;
  variant?: 'stroke' | 'ghost';
  size?: 'xs' | 's' | 'm' | 'l';
  icon?: ReactNode;
  endIcon?: ReactNode;
  iconPosition?: 'start' | 'top';
  prominent?: boolean;
  fullWidth?: boolean;
};

type ToggleButtonStyleContextValue = Pick<
  ToggleButtonProps,
  'variant' | 'size' | 'prominent' | 'iconPosition' | 'fullWidth'
>;

export const ToggleButtonStyleContext = createContext<
  ContextValue<ToggleButtonStyleContextValue, HTMLButtonElement>
>({});

/**
 * A toggle button allows a user to toggle a selection on or off, for example switching between two states or modes.
 *
 * https://react-spectrum.adobe.com/react-aria/ToggleButton.html
 *
 * ```tsx
 * <ToggleButton size="m" isSelected={isSelected} onChange={handleChange}>
 *    Currently {isSelected ? "selected" : "not selected"}
 * </ToggleButton>
 * ```
 */

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  (
    {
      children,
      size,
      icon,
      endIcon,
      iconPosition,
      variant,
      prominent,
      fullWidth,
      className,
      ...rest
    },
    ref,
  ) => {
    const hasChildren = Boolean(children);
    const [mergedProps, mergedRef] = useContextProps(
      {
        variant,
        size,
        prominent,
        iconPosition,
        fullWidth,
        className,
      },
      ref,
      ToggleButtonStyleContext,
    );

    const classes = toggleButtonVariants({
      variant: mergedProps.variant,
      size: mergedProps.size,
      prominent: mergedProps.prominent,
      iconPosition: mergedProps.iconPosition,
      fullWidth: mergedProps.fullWidth,
      className: mergedProps.className,
      withIcon: hasChildren && Boolean(icon),
      withEndIcon: hasChildren && Boolean(endIcon),
      iconOnly: !hasChildren && Boolean(icon),
    });
    return (
      <RACToggleButton {...rest} ref={mergedRef} className={classes}>
        {icon}
        {children}
        {endIcon}
      </RACToggleButton>
    );
  },
);
